import { useEffect, useMemo, useRef, useState } from "react";
import {
  Download,
  Home,
  Plus,
  Search,
  Send,
  Upload,
  X,
} from "lucide-react";
import { Link } from "react-router";
import type { ContactListItem } from "@guestbook/shared";
import { AddExistingContactsDialog } from "@/routes/app/contacts/AddExistingContactsDialog";
import { AddToHouseholdMenu } from "@/routes/app/contacts/AddToHouseholdMenu";
import { AddToListMenu } from "@/routes/app/contacts/AddToListMenu";
import { ContactsTableSkeleton } from "@/routes/app/contacts/ContactsTableSkeleton";
import { DeleteSelectedButton } from "@/routes/app/contacts/DeleteSelectedButton";
import { FilterMenu } from "@/routes/app/contacts/FilterMenu";
import { ToolbarButton } from "@/routes/app/contacts/ToolbarButton";
import {
  GRID_TEMPLATE,
  ROW_LINK_SPAN,
} from "@/routes/app/contacts/contactsTableLayout";
import { RemoveSelectedButton } from "@/routes/app/contacts/RemoveSelectedButton";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Badge } from "@/shared/components/ui/Badge";
import { Button } from "@/shared/components/ui/Button";
import { Checkbox } from "@/shared/components/ui/Checkbox";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { Input } from "@/shared/components/ui/Input";
import { Pagination } from "@/shared/components/ui/Pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/Tooltip";
import {
  EMPTY_CONTACTS_FILTERS,
  hasActiveFilters,
  useContactsList,
  type ContactsFilters,
} from "@/shared/lib/contacts";
import { useActiveScope, useScopedHref } from "@/shared/lib/scope";
import { cn } from "@/shared/lib/utils";

const PAGE_LIMIT = 50;

export function ContactsList() {
  const { listId, list: activeList } = useActiveScope();
  const href = useScopedHref();
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<ContactsFilters>(EMPTY_CONTACTS_FILTERS);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [addExistingOpen, setAddExistingOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  // The last row toggled by a plain click; shift-click selects the range
  // between it and the clicked row. Null when there's no active anchor.
  const anchorIdRef = useRef<string | null>(null);

  // Reset paging + selection when the active scope changes so we don't
  // page past the end of a different result set, or carry a selection from
  // one scope into another.
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
    anchorIdRef.current = null;
  }, [listId]);

  const filtersActive = hasActiveFilters(filters);
  const list = useContactsList({
    search: search || undefined,
    listId: listId ?? undefined,
    hasEmail: filters.hasEmail === "any" ? undefined : filters.hasEmail,
    hasPhone: filters.hasPhone === "any" ? undefined : filters.hasPhone,
    hasAddress: filters.hasAddress === "any" ? undefined : filters.hasAddress,
    tag: filters.tag || undefined,
    page,
    limit: PAGE_LIMIT,
  });
  const items = list.data?.items ?? [];
  const pageCount = Math.max(1, Math.ceil((list.data?.total ?? 0) / PAGE_LIMIT));

  // A search term or an active filter means a zero-result page is "filtered to
  // empty", not "nothing here yet". The toolbar (and the empty-state copy) key
  // off this so a truly-empty list hides the search/filter controls, while a
  // filtered-to-zero result keeps them so the user can clear back out.
  const hasFilters = search.length > 0 || filtersActive;
  const isTrulyEmpty =
    !list.isLoading && !list.isError && items.length === 0 && !hasFilters;

  // If the result set shrank (filtered/deleted) past the current page, snap
  // back to the last real page. Guarded on `list.data` so the transient
  // empty state while a page is refetching doesn't reset us to page 1.
  useEffect(() => {
    if (list.data && page > pageCount) setPage(pageCount);
  }, [list.data, page, pageCount]);

  const scopeLabel = listId
    ? activeList?.name ?? "Unknown list"
    : "All Contacts";

  function goToPage(next: number) {
    setPage(next);
    // Smooth-scroll back to the top so the next page starts in view rather
    // than leaving the viewport at the bottom of the previous page. Deferred to
    // the next frame so it runs *after* React commits the new rows — otherwise,
    // when the target page is already cached (e.g. paging back to page 1), the
    // synchronous DOM swap in this same tick cancels the smooth scroll.
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }
  function resetPaging() {
    setPage(1);
  }

  function openSearch() {
    setSearchOpen(true);
    // Focus runs in the next tick so the input has mounted.
    queueMicrotask(() => searchInputRef.current?.focus());
  }
  function closeSearch() {
    setSearchOpen(false);
    if (search) {
      setSearch("");
      resetPaging();
    }
  }

  function toggleRow(id: string, shiftKey: boolean) {
    const ids = items.map((i) => i.id);
    const anchor = anchorIdRef.current;
    // Shift-click extends from the anchor: add the inclusive range between the
    // anchor and the clicked row (in displayed order). Falls through to a plain
    // toggle when there's no anchor or it isn't on the current page.
    if (shiftKey && anchor) {
      const start = ids.indexOf(anchor);
      const end = ids.indexOf(id);
      if (start !== -1 && end !== -1) {
        const [lo, hi] = start <= end ? [start, end] : [end, start];
        setSelectedIds((prev) => {
          const next = new Set(prev);
          for (const rid of ids.slice(lo, hi + 1)) next.add(rid);
          return next;
        });
        return; // anchor stays fixed so the range can be re-extended
      }
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    anchorIdRef.current = id;
  }
  function togglePage(allOnPage: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPage) {
        for (const item of items) next.delete(item.id);
      } else {
        for (const item of items) next.add(item.id);
      }
      return next;
    });
    anchorIdRef.current = null;
  }

  const pageState = useMemo<"none" | "some" | "all">(() => {
    if (items.length === 0) return "none";
    const selectedOnPage = items.filter((i) => selectedIds.has(i.id)).length;
    if (selectedOnPage === 0) return "none";
    if (selectedOnPage === items.length) return "all";
    return "some";
  }, [items, selectedIds]);

  return (
    <Section spacing="md">
      <Container size="6xl">
        <div className="flex flex-col gap-8">
          <h1 className="type-heading-lg text-text">{scopeLabel}</h1>

          <div
            className={cn(
              "-mx-4 flex gap-3 overflow-x-auto px-4 sm:mx-0 sm:px-0",
              "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
            )}
          >
            {activeList ? (
              <Button
                className="shrink-0 shadow-none"
                onClick={() => setAddExistingOpen(true)}
              >
                <Plus aria-hidden className="h-4 w-4" />
                Add Contact
              </Button>
            ) : (
              <Button asChild className="shrink-0 shadow-none">
                <Link to={href("/app/contacts/new")}>
                  <Plus aria-hidden className="h-4 w-4" />
                  Add Contact
                </Link>
              </Button>
            )}
            <Button asChild variant="secondary" className="shrink-0">
              <Link to={href("/app/contacts/import")}>
                <Upload aria-hidden className="h-4 w-4" />
                Import Contacts
              </Link>
            </Button>
            <Button asChild variant="secondary" className="shrink-0">
              <Link to="/app/contacts/collect">
                <Send aria-hidden className="h-4 w-4" />
                Collect Contacts
              </Link>
            </Button>
            <Button asChild variant="secondary" className="shrink-0">
              <Link to="/app/contacts/households">
                <Home aria-hidden className="h-4 w-4" />
                Manage Households
              </Link>
            </Button>
            <Button asChild variant="secondary" className="shrink-0">
              <Link to={href("/app/contacts/export")}>
                <Download aria-hidden className="h-4 w-4" />
                Export Contacts
              </Link>
            </Button>
          </div>

          {!isTrulyEmpty ? (
            <div className="hidden items-center justify-end gap-3 sm:flex">
              {searchOpen ? (
                <Input
                  ref={searchInputRef}
                  type="search"
                  placeholder="Search by name…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    resetPaging();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") closeSearch();
                  }}
                  className="max-w-sm"
                />
              ) : null}

              <FilterMenu
                value={filters}
                onChange={(next) => {
                  setFilters(next);
                  resetPaging();
                }}
              />

              <ToolbarButton
                aria-label={searchOpen ? "Close search" : "Search contacts"}
                aria-expanded={searchOpen}
                onClick={searchOpen ? closeSearch : openSearch}
              >
                {searchOpen ? (
                  <X aria-hidden className="h-4 w-4" />
                ) : (
                  <Search aria-hidden className="h-4 w-4" />
                )}
              </ToolbarButton>
            </div>
          ) : null}

          {activeList ? (
            <AddExistingContactsDialog
              list={activeList}
              open={addExistingOpen}
              onOpenChange={setAddExistingOpen}
            />
          ) : null}

          {selectedIds.size > 0 ? (
            <div
              className={cn(
                "fixed bottom-6 left-4 right-4 z-toast",
                "sm:left-1/2 sm:right-auto sm:w-auto sm:-translate-x-1/2 sm:max-w-[calc(100vw-2rem)]",
                "flex items-center justify-between gap-2 rounded-2xl glass-surface px-4 py-3",
              )}
              role="region"
              aria-label="Bulk actions"
            >
              <div className="flex items-center gap-1 shrink-0">
                <span className="type-label-base text-text">
                  {selectedIds.size} selected
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setSelectedIds(new Set())}
                  aria-label="Clear selection"
                >
                  <X aria-hidden className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <AddToListMenu
                  selectedIds={[...selectedIds]}
                  onDone={() => setSelectedIds(new Set())}
                />
                <AddToHouseholdMenu
                  selectedIds={[...selectedIds]}
                  onDone={() => setSelectedIds(new Set())}
                />
                {listId ? (
                  <RemoveSelectedButton
                    listId={listId}
                    selectedIds={[...selectedIds]}
                    onDone={() => setSelectedIds(new Set())}
                  />
                ) : null}
                <DeleteSelectedButton
                  selectedIds={[...selectedIds]}
                  onDone={() => setSelectedIds(new Set())}
                />
              </div>
            </div>
          ) : null}

          <div
            className={cn(
              "transition-opacity duration-300",
              list.isPlaceholderData && "opacity-60",
            )}
          >
            {list.isLoading ? (
              <ContactsTableSkeleton rows={8} />
            ) : list.isError ? (
              <p className="type-body-base text-danger-strong">
                Couldn't load contacts: {list.error.message}
              </p>
            ) : items.length === 0 ? (
              <EmptyState
                title={
                  hasFilters
                    ? "No matching contacts"
                    : listId
                    ? "This list has no members yet"
                    : "No contacts yet"
                }
                description={
                  hasFilters
                    ? "Try clearing or changing your search and filters to see everyone."
                    : listId
                    ? "Select contacts on the All Contacts view and use the bulk action to add them to this list."
                    : "Add your first contact, import a spreadsheet, or send a share link to start collecting addresses."
                }
                action={
                  hasFilters || listId ? null : (
                    <Button asChild>
                      <Link to={href("/app/contacts/new")}>Add a contact</Link>
                    </Button>
                  )
                }
              />
            ) : (
              <ContactsTable
                items={items}
                selectedIds={selectedIds}
                onToggleRow={toggleRow}
                pageState={pageState}
                onTogglePage={() => togglePage(pageState === "all")}
                scopedHref={href}
              />
            )}
          </div>

          {pageCount > 1 && !list.isLoading ? (
            <div className="flex flex-col items-center gap-2">
              <Pagination
                page={page}
                pageCount={pageCount}
                onPageChange={goToPage}
              />
              <p className="type-body-sm text-text-muted">
                Page {page} of {pageCount}
              </p>
            </div>
          ) : null}
        </div>
      </Container>
    </Section>
  );
}

interface ContactsTableProps {
  items: ContactListItem[];
  selectedIds: Set<string>;
  onToggleRow: (id: string, shiftKey: boolean) => void;
  pageState: "none" | "some" | "all";
  onTogglePage: () => void;
  scopedHref: (path: string) => string;
}

function ContactsTable({
  items,
  selectedIds,
  onToggleRow,
  pageState,
  onTogglePage,
  scopedHref,
}: ContactsTableProps) {
  const headerChecked: boolean | "indeterminate" =
    pageState === "all" ? true : pageState === "some" ? "indeterminate" : false;

  // Radix Checkbox composes our onClick before firing onCheckedChange, so we
  // stash the shift modifier here and read it back when the change fires.
  const shiftHeldRef = useRef(false);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="overflow-hidden rounded-xl border border-border-muted glass-surface-subtle">
        <div
          className={cn(
            "grid items-center gap-4 border-b border-border-muted px-4 py-2.5",
            "type-label-sm tracking-wider uppercase text-text-muted",
            GRID_TEMPLATE,
          )}
        >
          <span className="flex items-center">
            <Checkbox
              checked={headerChecked}
              onCheckedChange={onTogglePage}
              aria-label={
                pageState === "all" ? "Deselect all on page" : "Select all on page"
              }
            />
          </span>
          <span>Name</span>
          <span className="hidden md:block">Email</span>
          <span className="hidden lg:block">Phone</span>
          <span className="hidden md:block">Address</span>
          <span className="hidden xl:block">Tags</span>
        </div>
        <ul className="flex flex-col">
          {items.map((c) => {
            const selected = selectedIds.has(c.id);
            const name = c.name || "—";
            const householdName = c.household?.displayName ?? null;
            const tagOverflow = Math.max(0, c.tags.length - 2);
            return (
              <li
                key={c.id}
                className={cn(
                  "grid items-center gap-4 border-t border-border-muted px-4 first:border-t-0",
                  "transition-colors duration-150 hover:bg-surface-muted/40",
                  selected && "bg-surface-muted/30",
                  GRID_TEMPLATE,
                )}
              >
                <span className="flex items-center py-2.5">
                  <Checkbox
                    checked={selected}
                    onClick={(e) => {
                      shiftHeldRef.current = e.shiftKey;
                    }}
                    onCheckedChange={() => onToggleRow(c.id, shiftHeldRef.current)}
                    aria-label={`Select ${name}`}
                  />
                </span>
                <Link
                  to={scopedHref(`/app/contacts/${c.id}`)}
                  className={cn(
                    "grid grid-cols-subgrid items-center py-2.5",
                    ROW_LINK_SPAN,
                  )}
                >
                  <span className="truncate type-body-base text-text">{name}</span>
                  <span className="hidden truncate type-body-sm text-text-muted md:block">
                    {c.email ?? "—"}
                  </span>
                  <span className="hidden truncate type-body-sm text-text-muted lg:block">
                    {c.phone ?? "—"}
                  </span>
                  <span className="hidden min-w-0 items-center gap-1.5 type-body-sm text-text-muted md:flex">
                    {c.household ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className="inline-flex shrink-0 text-text-muted"
                            aria-label="Address from household"
                          >
                            <Home aria-hidden className="h-3.5 w-3.5" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {householdName ?? "Household"}
                        </TooltipContent>
                      </Tooltip>
                    ) : null}
                    <span className="truncate">{c.addressLine ?? "—"}</span>
                  </span>
                  <span className="hidden min-w-0 items-center gap-1 xl:flex">
                    {c.tags.length === 0 ? (
                      <span className="type-body-sm text-text-muted">—</span>
                    ) : (
                      <>
                        {c.tags.slice(0, 2).map((tag) => (
                          <Badge key={tag} variant="muted" className="min-w-0">
                            <span className="truncate">{tag}</span>
                          </Badge>
                        ))}
                        {tagOverflow > 0 ? (
                          <Badge
                            variant="muted"
                            className="shrink-0 bg-transparent px-1"
                          >
                            {`+${tagOverflow}`}
                          </Badge>
                        ) : null}
                      </>
                    )}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </TooltipProvider>
  );
}
